/* eslint-disable camelcase */
import React, { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import CardGrid from './CardGrid';
import HowPowerWorks from './HowPowerWorks';
import TopBanner from './TopBanner';
import Footer from './Footer';
import GlobalPrivilegesBanner from '../../../components/Banners/GlobalPrivileges';
import FableticsBanner from '../../../components/Banners/Fabletics';
import PlayStayStyleBanner from '../../../components/Banners/PlayStayStyle';
import useFabletics from '../../../components/hooks/useFabletics';
import { PowerFunnelSurfaces } from '../../../components/Analytics/constants';

import { titles, userParams } from '../../../components/PopUps/LoginPopUpWrapper/LoginPopUp/utils';

import { showLoginPopUp } from '../../../actions/login-popup-actions';
import { getPrismicDoc } from '../../../actions/prismic-actions';
import { useStatsig } from '../../../components/hooks/useStatsig';
import useStartPowerCheckout from '../../../components/hooks/useStartPowerCheckout';
import { useBraze } from '../../../components/hooks/useBraze';
import { getPowerSubscriptionPlans } from '../../../api/checkoutApi';
import { getApiAxiosInstance } from '../../../utils/network';
import head from '../../../components/Meta';
import config from '../../../../config';

import styles from './powerPerksPage.scss';

function PowerPerksPage(props) {
    const [openCard, setOpenCard] = useState(null);
    const [perkCards, setPerkCards] = useState([]);
    const [powerBenefitCards, setPowerBenefitCards] = useState([]);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [isLoadingPlans, setIsLoadingPlans] = useState(false);
    const auth = useSelector(({ auth }) => auth);
    const prismic = useSelector(({ prismic }) => prismic);
    const perksRef = useRef(null);
    const dispatch = useDispatch();
    const { startPowerCheckout } = useStartPowerCheckout();
    const isPowered = auth.user?.isPowered;
    const hasPastDueSubscription = auth.user?.hasPastDueSubscription;

    const parsed = queryString.parse(props.location.search);
    const { power_perks } = prismic;
    const { initializeStatsigUser, checkGate, featureGates } = useStatsig();
    const { brazeInstance, logPowerCampaignEvent, eventNames } = useBraze();
    const displayGlobalPrivilegesFeature = checkGate(featureGates.POWER_UPGRADE_MODAL_V4);
    const { enabled: isFableticsEnabled } = useFabletics();
    const isPlayStayStyleBannerEnabled = !auth?.user &&
        checkGate(featureGates.POWER_PERKS_PLAY_STAY_STYLE_BANNER);

    const fetchSubscriptionPlans = async () => {
        setIsLoadingPlans(true);
        try {
            const response = await getPowerSubscriptionPlans(getApiAxiosInstance());
            if (response?.data?.subscriptionPlans) {
                setSubscriptionPlans(response.data.subscriptionPlans);
            }
        } catch (error) {
            // Keep subscriptionPlans as empty array on error
            setSubscriptionPlans([]);
        } finally {
            setIsLoadingPlans(false);
        }
    };

    useEffect(() => {
        if (!auth.user) {
            const userParamsNeeded = [
                userParams.CLAIMED_PROFILE,
            ];
            const popupData = {
                userParamsNeeded,
                titles: titles.POWER_PERKS,
                type: 'power_perks',
                showCloseIcon: false,
                closeOnClickOutside: true,
            };
            dispatch(showLoginPopUp(popupData));
        }

        if (!prismic.power_perks) {
            dispatch(getPrismicDoc('power_perks', 'power_perks'));
        }
    }, []);

    useEffect(() => {
        if (auth?.user) {
            initializeStatsigUser();
        }
    }, [auth?.user]);

    // Determine what pricing to show based on feature flag and auth status
    const getEffectivePricingData = () => {
        const isCountryPricingEnabled = checkGate(featureGates.COUNTRY_PRICING);
        const isAuthenticated = !!auth?.user;

        if (isCountryPricingEnabled) {
            if (isAuthenticated) {
                // FF ON + Authenticated: Show country prices
                return {
                    shouldShowPricing: true,
                    shouldFetchPlans: true,
                    subscriptionPlans,
                    isLoadingPlans,
                };
            }
            // FF ON + Unauthenticated: Do not show any price
            return {
                shouldShowPricing: false,
                shouldFetchPlans: false,
                subscriptionPlans: [],
                isLoadingPlans: false,
            };
        }
        // FF OFF: Show default USD price ($10/month) for both auth states
        const defaultPlans = [{
            interval: 'year',
            displayMonthlyAmount: '$10',
            displayAmountPayPerRecurringInterval: '$120',
            savingsPercentage: 17,
        }, {
            interval: 'month',
            displayAmountPayPerRecurringInterval: '$10',
        }];

        return {
            shouldShowPricing: true,
            shouldFetchPlans: false,
            subscriptionPlans: defaultPlans,
            isLoadingPlans: false,
        };
    };

    const effectivePricingData = getEffectivePricingData();

    // Determine which popup to show when clicking "Start Power"
    const handlePowerClick = (gatedFeature) => {
        const isAuthenticated = !!auth?.user;

        if (!isAuthenticated) {
            // Unauthenticated: Show login popup
            const userParamsNeeded = [
                userParams.CLAIMED_PROFILE,
            ];
            const popupData = {
                userParamsNeeded,
                titles: titles.POWER_PERKS,
                type: 'power_perks',
                showCloseIcon: true,
                closeOnClickOutside: true,
            };
            dispatch(showLoginPopUp(popupData));
        } else {
            startPowerCheckout({
                surface: PowerFunnelSurfaces.POWER_PERKS_PAGE,
                gatedFeature,
            });
        }
    };

    useEffect(() => {
        if (!isPowered && effectivePricingData.shouldFetchPlans) {
            fetchSubscriptionPlans();
        }
    }, [isPowered, auth?.user, effectivePricingData.shouldFetchPlans]);

    useEffect(() => {
        if (parsed.to === 'perks') {
            perksRef.current.scrollIntoView(true);
        }
    }, [parsed.to, perksRef.current]);

    useEffect(() => {
        if (power_perks?.power_perks_subscription_card_benefits?.[0]) {
            const orderedCards = power_perks?.power_perks_subscription_card_benefits?.[0]?.repeatable ? [...power_perks.power_perks_subscription_card_benefits[0].repeatable] : [];
            const sortedOrderedCards = orderedCards.toSorted((a, b) => a.order?.value - b.order?.value);

            setPowerBenefitCards(sortedOrderedCards);
        }
    }, [power_perks?.power_perks_subscription_card_benefits?.[0]]);

    useEffect(() => {
        if (power_perks?.power_perk_card?.[0]) {
            const orderedCards = power_perks?.power_perk_card?.[0]?.repeatable ? [...power_perks.power_perk_card[0].repeatable] : [];
            const sortedOrderedCards = orderedCards.toSorted((a, b) => a.order?.value - b.order?.value);

            setPerkCards(sortedOrderedCards);
        }
    }, [power_perks?.power_perk_card?.[0]]);

    useEffect(() => {
        if (auth?.user?.id && brazeInstance) {
            logPowerCampaignEvent({
                search: props?.location?.search,
                eventName: eventNames.POWER_PERKS_PAGE,
                memberId: auth?.user?.id,
            });
        }
    }, [auth?.user?.id, brazeInstance]);

    const clearOpenCard = () => {
        setOpenCard();
    };

    return (
        <div className={styles.page}>
            {head({
                title: 'Tennis Analytics, Pickleball Analytics, Tournament Discounts | UTR Power',
                description: 'Unlock advanced tennis and pickleball analytics, tournament discounts, and exclusive perks with UTR Sports Power Subscriptions. Track progress, scout opponents, and save money.',
                image: `https://www.${config.siteBaseUrl}/img/utr_sports/utr-sports-logo.svg`,
                imageWidth: '930',
                imageHeight: '395',
            })}
            {isPlayStayStyleBannerEnabled ? (
                <PlayStayStyleBanner onSignInClick={() => handlePowerClick()} />
            ) : (
                <>
                    {!isFableticsEnabled && !displayGlobalPrivilegesFeature && <TopBanner auth={auth} />}
                    {displayGlobalPrivilegesFeature && (
                        isFableticsEnabled
                            ? <FableticsBanner surface={PowerFunnelSurfaces.POWER_PERKS_PAGE} />
                            : <GlobalPrivilegesBanner surface={PowerFunnelSurfaces.POWER_PERKS_PAGE} />
                    )}
                </>
            )}
            <div className={`container`}>
                {isPowered ? (
                    // Powered users: Show perks first, then benefits
                    <>
                        <div className="row">
                            <div className={`col`}>
                                <div className="row mt48">
                                    <div className="col-md-10 offset-md-1" ref={perksRef}>
                                        <CardGrid
                                            isPowered={isPowered}
                                            openCard={openCard}
                                            clearOpenCard={clearOpenCard}
                                            cards={perkCards}
                                            cardsTitle={'Exclusive Power Perks'}
                                            cardsDescription={'Access partner benefits like discounts on gear, apparel, and more.'}
                                            cardButtonText={'REDEEM PERK'}
                                            ctaVerb={'Redeem'}
                                            mobileCtaText={'Redeem Perk'}
                                            data-testid="power-perks.exclusive-perks"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className={`col`}>
                                <div className="row mt48">
                                    <div className="col-md-10 offset-md-1">
                                        <CardGrid
                                            isPowered={isPowered}
                                            openCard={openCard}
                                            clearOpenCard={clearOpenCard}
                                            cards={powerBenefitCards}
                                            cardsTitle={power_perks?.power_perks_subscription_card_benefits?.[0].power_subscription_benefits_title}
                                            cardButtonText={'Explore'}
                                            ctaVerb={'Access'}
                                            mobileCtaText={'Learn More'}
                                            data-testid="power-perks.power-benefits"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    // Free users: Show benefits first, then perks
                    <>
                        <div className="row">
                            <div className={`col`}>
                                <div className="row mt48">
                                    <div className="col-md-10 offset-md-1" ref={perksRef}>
                                        <CardGrid
                                            isPowered={isPowered}
                                            openCard={openCard}
                                            clearOpenCard={clearOpenCard}
                                            cards={powerBenefitCards}
                                            cardsTitle={power_perks?.power_perks_subscription_card_benefits?.[0].power_subscription_benefits_title}
                                            cardButtonText={'Explore'}
                                            ctaVerb={'Access'}
                                            mobileCtaText={'Learn More'}
                                            data-testid="power-perks.power-benefits"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`row`}>
                            <div className={`col`}>
                                <div className="row mt48">
                                    <div className="col-md-10 offset-md-1">
                                        <CardGrid
                                            isPowered={isPowered}
                                            openCard={openCard}
                                            clearOpenCard={clearOpenCard}
                                            cards={perkCards}
                                            cardsTitle={'Exclusive Power Perks'}
                                            cardsDescription={'Access partner benefits like discounts on gear, apparel, and more.'}
                                            cardButtonText={'REDEEM PERK'}
                                            ctaVerb={'Redeem'}
                                            mobileCtaText={'Redeem Perk'}
                                            data-testid="power-perks.exclusive-perks"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {(!isPowered || hasPastDueSubscription) && (
                            <div className="row">
                                <div className="col-md-10 offset-md-1">
                                    <HowPowerWorks
                                        auth={auth}
                                        subscriptionPlans={effectivePricingData.subscriptionPlans}
                                        isLoadingPlans={effectivePricingData.isLoadingPlans}
                                        shouldShowPricing={effectivePricingData.shouldShowPricing}
                                        onPowerClick={handlePowerClick}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            {(!isPowered || hasPastDueSubscription) && (
                <Footer
                    auth={auth}
                    subscriptionPlans={effectivePricingData.subscriptionPlans}
                    isLoadingPlans={effectivePricingData.isLoadingPlans}
                    shouldShowPricing={effectivePricingData.shouldShowPricing}
                    onPowerClick={handlePowerClick}
                />
            )}
        </div>
    );
}

PowerPerksPage.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string.isRequired,
    }).isRequired,
};

export default withRouter(PowerPerksPage);
